// hooks/useFirebaseMessaging.js
import React, { useEffect } from 'react'
import { getToken, onMessage } from 'firebase/messaging'
import { messagingApp } from '../utils/firebase'
import { UAParser } from 'ua-parser-js'
import { v4 as uuidv4 } from 'uuid'
import { useFirebaseToken } from './useNotification'
import { useTrackEvent } from '../utils/analyticEvents'
import { GA_EVENTS, CMS_EVENTS} from '../utils/gaConstants'
import { updateFcmToken } from 'shared-ui/redux/reducers/loginReducer'
import { useDispatch } from 'react-redux'
import { useSnackBar } from 'shared-ui/components'
import { registerDeviceApi, getDeviceInfo } from '../api/deviceRegistrationApi'
import { getQueryClient } from '../utils/logoutUtils'
import { clearApiGetCache } from '../api/apiWrapper'
import { QUERY_KEY } from '../utils/constants'

const VAPID_KEY =
    'BIWNeThlaSESVSIyitVgarCMZue0Gjkl7c8jF5ckUDd4DERF_VobjTqQhwSmgdxtcsXt_gT2z1A3FcA-k_222b8'

export const useFirebaseMessaging = (isAuthenticated) => {
    // const { mutate: deviceMutate } = useFirebaseToken()
    const trackEvent = useTrackEvent()
    const dispatch = useDispatch()
    const { showSnackBar } = useSnackBar()

    useEffect(() => {
        const requestPermissionAndToken = async () => {
            try {
                if (!('Notification' in window)) {
                    console.error('This browser does not support desktop notifications');
                    dispatch(updateFcmToken('FAILED'))
                    localStorage.setItem('fcmToken', 'FAILED')
                    return;
                }

                if (!('serviceWorker' in navigator)) {
                    console.error('This browser does not support Service Workers');
                    dispatch(updateFcmToken('FAILED'))
                    localStorage.setItem('fcmToken', 'FAILED')
                    return;
                }

                let permission = Notification.permission;

                if (permission === 'default') {
                    permission = await Notification.requestPermission();
                }

                if (permission !== 'granted') {
                    console.warn('Permission not granted:', permission);
                    dispatch(updateFcmToken('FAILED'))
                    localStorage.setItem('fcmToken', 'FAILED')
                    return;
                }

                try {
                    // Try relative path with explicit scope
                    const registration = await navigator.serviceWorker.register('firebase-messaging-sw.js?v=6', {
                        scope: '/'
                    });

                    await navigator.serviceWorker.ready;

                    const fcmToken = await getToken(messagingApp, {
                        vapidKey: VAPID_KEY,
                        serviceWorkerRegistration: registration
                    });
                    console.log('fcmToken', fcmToken)

                    if (fcmToken) {
                        dispatch(updateFcmToken(fcmToken))
                        localStorage.setItem('fcmToken', fcmToken)
                        const deviceId = localStorage.getItem('deviceId') || uuidv4();
                        
                        if (isAuthenticated) {
                            const parser = new UAParser();
                            const { browser, os, device } = parser.getResult();
                            const deviceName = device.model
                                ? `${device.vendor || ''} ${device.model}`.trim()
                                : `${os.name || ''} ${browser.name || ''}`.trim();
                            const finalDeviceName = deviceName || 'Web Device';
                        } else {
                            // User not authenticated, skipping backend sync
                        }
                    } else {
                        console.warn('FCM token was not generated.');
                        dispatch(updateFcmToken('FAILED'))
                        localStorage.setItem('fcmToken', 'FAILED')
                    }
                } catch (swError) {
                    console.error('Service Worker registration failed:', swError);
                    // Fallback to absolute path if relative fails
                    try {
                        const fallbackReg = await navigator.serviceWorker.register('/firebase-messaging-sw.js?v=6', { scope: '/' });
                        // Try getting token with fallback registration
                        await navigator.serviceWorker.ready;
                        const fcmToken = await getToken(messagingApp, {
                            vapidKey: VAPID_KEY,
                            serviceWorkerRegistration: fallbackReg
                        });
                        if (fcmToken) {
                            dispatch(updateFcmToken(fcmToken))
                            localStorage.setItem('fcmToken', fcmToken)
                        } else {
                            dispatch(updateFcmToken('FAILED'))
                            localStorage.setItem('fcmToken', 'FAILED')
                        }
                    } catch (err) {
                        console.error('Final fallback SW registration failed:', err);
                        dispatch(updateFcmToken('FAILED'))
                        localStorage.setItem('fcmToken', 'FAILED')
                    }
                }
            } catch (error) {
                console.error('Error during notification setup:', error)
                dispatch(updateFcmToken('FAILED'))
                localStorage.setItem('fcmToken', 'FAILED')
            }
        }

        requestPermissionAndToken()

        // Handle foreground notifications
        let unsubscribe = () => { };
        try {
            unsubscribe = onMessage(messagingApp, (payload) => {
                //console.log('FCM foreground payload:', payload)  
                const action = payload.data?.action;
                const notificationBody = payload.notification?.body || payload.data?.body || payload.data?.message || 'New message';
                trackEvent(GA_EVENTS.coins_earned, {
                  coins_earned: parseInt(payload?.data?.coinValue, 10) || 0,
                  task_type: 'coined_earn'
                });

                if (action === 'coin_milestone_alert') {
                    return;
                }

                const match = notificationBody.match(/^\+\d+/);

                const formattedMessage = match ? (
                    <>
                        <span style={{ color: '#5CB617', fontWeight: 600 }}>{match[0]}</span>
                        {notificationBody.slice(match[0].length)}
                    </>
                ) : notificationBody;

                showSnackBar({
                    ...payload.data,
                    message: formattedMessage
                });

                if (match || action === 'reward_alert') {
                    clearApiGetCache()
                    const queryClient = getQueryClient()
                    if (queryClient) {
                        queryClient.invalidateQueries({ queryKey: [QUERY_KEY.GetCoinBalance] })
                        queryClient.invalidateQueries({ queryKey: [QUERY_KEY.GetCreditBalance] })
                    }
                }

                if (typeof window !== 'undefined') {
                    window.dispatchEvent(
                        new CustomEvent('NEW_NOTIFICATION_RECEIVED', { detail: payload })
                    )
                }
                const notifQueryClient = getQueryClient()
                if (notifQueryClient) {
                    notifQueryClient.invalidateQueries({
                        queryKey: [QUERY_KEY.GET_NOTIFICATIONS]
                    })
                }


                if (Notification.permission === 'granted') {
                    const notificationTitle = payload.notification?.title || payload.data?.title || 'New Notification';
                    const notificationOptions = {
                        body: notificationBody,
                        icon: '/logo192.png',
                        data: payload.data || {}
                    };
                    const notification = new Notification(notificationTitle, notificationOptions);
                    notification.onclick = (event) => {
                        event.preventDefault(); // prevent the browser from focusing the Notification's tab
                        const type = payload.data?.type || 'background';
                        const messageId = payload.messageId || 'unknown';

                        trackEvent(GA_EVENTS.notification_clicked, {
                            notification_type: type,
                            campaign_id: messageId,
                            time_since_sent_hours: '0'
                        });

                        window.focus();
                    };
                }
            })
        } catch (msgError) {
            console.error('❌ Error setting up onMessage listener:', msgError);
        }

        return () => unsubscribe()
    }, [isAuthenticated]);
}
